import { useCallback, useRef, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format, addWeeks } from 'date-fns';
import { useUserStore } from '@/store/userStore';
import { scheduleClassesService } from '@/services/scheduleClassesService';
import type { ScheduledClass } from '@/types/types';

/**
 * Options for the useScheduleClasses hook
 */
export interface UseScheduleClassesOptions {
  /** Start date for filtering classes */
  startDate?: Date;
  /** End date for filtering classes */
  endDate?: Date;
  /** Optional calendar id to filter classes */
  calendarId?: string | null;
  /** Whether to automatically fetch classes on mount and when dates change */
  autoFetch?: boolean;
  /** Whether to enable prefetching for adjacent weeks */
  enablePrefetch?: boolean;
  /** Callback when fetch succeeds */
  onFetchSuccess?: (classes: ScheduledClass[]) => void;
  /** Callback when fetch fails */
  onFetchError?: (error: Error) => void;
}

/**
 * Return type for the useScheduleClasses hook
 */
export interface UseScheduleClassesReturn {
  /** Array of scheduled classes */
  classes: ScheduledClass[];
  /** Whether data is currently being loaded */
  isLoading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Function to fetch classes for specific date range */
  fetchClasses: (startDate?: Date, endDate?: Date) => Promise<void>;
  /** Function to force refresh (bypass cache) */
  refreshClasses: () => Promise<void>;
  /** Function to invalidate cache for current user/gym */
  invalidateCache: () => void;
  /** Function to prefetch next week's classes */
  prefetchNextWeek: () => void;
  /** Function to prefetch previous week's classes */
  prefetchPrevWeek: () => void;
}

/**
 * Hook for fetching and managing scheduled classes with React Query
 * 
 * Benefits:
 * - Intelligent caching with automatic invalidation
 * - Request deduplication (multiple calls = single request)
 * - Stable dependencies (no infinite loops)
 * - Automatic refetching on stale data
 * - Background prefetching for better UX
 * 
 * @param options Configuration options
 * @returns Object containing classes, loading state, error, and fetch functions
 */
export const useScheduleClasses = (options: UseScheduleClassesOptions = {}): UseScheduleClassesReturn => {
  const {
    startDate,
    endDate,
    calendarId = null,
    autoFetch = true,
    enablePrefetch = false,
    onFetchSuccess,
    onFetchError,
  } = options;

  const user = useUserStore((state) => state.user);
  const gymId = useUserStore((state) => state.gymId);
  const queryClient = useQueryClient();

  // Store callbacks in refs to avoid dependency changes
  const onFetchSuccessRef = useRef(onFetchSuccess);
  const onFetchErrorRef = useRef(onFetchError);

  useEffect(() => {
    onFetchSuccessRef.current = onFetchSuccess;
    onFetchErrorRef.current = onFetchError;
  }, [onFetchSuccess, onFetchError]);

  const startDateStr = startDate ? format(startDate, 'yyyy-MM-dd') : null;
  const endDateStr = endDate ? format(endDate, 'yyyy-MM-dd') : null;
  const calendarIdStable = calendarId ?? null;

  const query = useQuery({
    queryKey: ['schedule-classes', user?.id, gymId, startDateStr, endDateStr, calendarIdStable],
    queryFn: async () => {
      if (!user?.id || !gymId) {
        throw new Error('Missing user or gymId');
      }

      console.log('[useScheduleClasses] Fetching via React Query', {
        userId: user.id,
        gymId,
        startDate: startDateStr,
        endDate: endDateStr,
        calendarId: calendarIdStable,
      });

      const classes = await scheduleClassesService.fetchClassesWithUserContext(
        user.id,
        gymId,
        startDate,
        endDate,
        false,
        calendarIdStable
      );

      // Call success callback if provided
      if (onFetchSuccessRef.current) {
        onFetchSuccessRef.current(classes);
      }

      return classes;
    },
    enabled: autoFetch && !!user?.id && !!gymId && !!startDate && !!endDate,
    staleTime: 30000, // 30 seconds before considering data stale
    gcTime: 5 * 60 * 1000, // 5 minutes garbage collection time
    refetchOnWindowFocus: false,
    retry: 3, // ✅ Augmenté à 3 retries
    retryDelay: (attemptIndex) => Math.min(1000 * Math.pow(2, attemptIndex), 10000), // ✅ Exponential backoff
    // ✅ Garde les données stale en cas d'erreur
    placeholderData: (previousData) => previousData,
  });

  // Handle errors via callback
  useEffect(() => {
    if (query.error && onFetchErrorRef.current) {
      onFetchErrorRef.current(query.error as Error);
    }
  }, [query.error]);

  /**
   * Fetch classes for specific date range
   * ✅ Stable function via useCallback with only primitive dependencies
   */
  const fetchClasses = useCallback(
    async (newStartDate?: Date, newEndDate?: Date) => {
      if (!user?.id || !gymId) {
        console.warn('[useScheduleClasses] Cannot fetch: missing user or gymId');
        return;
      }

      const targetStartDate = newStartDate || startDate;
      const targetEndDate = newEndDate || endDate;

      if (!targetStartDate || !targetEndDate) {
        console.warn('[useScheduleClasses] Cannot fetch: missing dates');
        return;
      }

      const targetStartStr = format(targetStartDate, 'yyyy-MM-dd');
      const targetEndStr = format(targetEndDate, 'yyyy-MM-dd');

      console.log('[useScheduleClasses] Manual fetch requested', {
        userId: user.id,
        gymId,
        startDate: targetStartStr,
        endDate: targetEndStr,
      });

      await queryClient.invalidateQueries({
        queryKey: ['schedule-classes', user.id, gymId, targetStartStr, targetEndStr, calendarIdStable],
      });

      await queryClient.refetchQueries({
        queryKey: ['schedule-classes', user.id, gymId, targetStartStr, targetEndStr, calendarIdStable],
      });
    },
    [user?.id, gymId, startDateStr, endDateStr, calendarIdStable, queryClient]
  );

  /**
   * Force refresh (bypass cache)
   */
  const refreshClasses = useCallback(async () => {
    console.log('[useScheduleClasses] Force refresh requested');
    await query.refetch();
  }, [query]);

  /**
   * Invalidate cache for current user/gym
   */
  const invalidateCache = useCallback(() => {
    if (!user?.id || !gymId) return;

    console.log('[useScheduleClasses] Invalidating cache', {
      userId: user.id,
      gymId,
    });

    scheduleClassesService.invalidateCache(user.id, gymId);

    queryClient.invalidateQueries({
      queryKey: ['schedule-classes', user.id, gymId],
    });
  }, [user?.id, gymId, queryClient]);

  /**
   * Prefetch next week's classes
   */
  const prefetchNextWeek = useCallback(() => {
    if (!user?.id || !gymId || !startDate || !endDate || !enablePrefetch) return;

    const nextWeekStart = addWeeks(startDate, 1);
    const nextWeekEnd = addWeeks(endDate, 1);
    const nextStartStr = format(nextWeekStart, 'yyyy-MM-dd');
    const nextEndStr = format(nextWeekEnd, 'yyyy-MM-dd');

    console.log('[useScheduleClasses] Prefetching next week', {
      startDate: nextStartStr,
      endDate: nextEndStr,
    });

    queryClient.prefetchQuery({
      queryKey: ['schedule-classes', user.id, gymId, nextStartStr, nextEndStr, calendarIdStable],
      queryFn: () =>
        scheduleClassesService.fetchClassesWithUserContext(
          user.id,
          gymId,
          nextWeekStart,
          nextWeekEnd,
          false,
          calendarIdStable
        ),
      staleTime: 30000,
    });
  }, [user?.id, gymId, startDateStr, endDateStr, calendarIdStable, enablePrefetch, queryClient]);

  /**
   * Prefetch previous week's classes
   */
  const prefetchPrevWeek = useCallback(() => {
    if (!user?.id || !gymId || !startDate || !endDate || !enablePrefetch) return;

    const prevWeekStart = addWeeks(startDate, -1);
    const prevWeekEnd = addWeeks(endDate, -1);
    const prevStartStr = format(prevWeekStart, 'yyyy-MM-dd');
    const prevEndStr = format(prevWeekEnd, 'yyyy-MM-dd');

    console.log('[useScheduleClasses] Prefetching previous week', {
      startDate: prevStartStr,
      endDate: prevEndStr,
    });

    queryClient.prefetchQuery({
      queryKey: ['schedule-classes', user.id, gymId, prevStartStr, prevEndStr, calendarIdStable],
      queryFn: () =>
        scheduleClassesService.fetchClassesWithUserContext(
          user.id,
          gymId,
          prevWeekStart,
          prevWeekEnd,
          false,
          calendarIdStable
        ),
      staleTime: 30000,
    });
  }, [user?.id, gymId, startDateStr, endDateStr, calendarIdStable, enablePrefetch, queryClient]);

  return {
    classes: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    fetchClasses,
    refreshClasses,
    invalidateCache,
    prefetchNextWeek,
    prefetchPrevWeek,
  };
};
